// v0.4.0: Milestone 3 — WebRTC client for the Cloudflare Realtime SFU.
// v0.13.0: TWO peer connections. `pub` only ever publishes (we offer, SFU answers);
// `sub` only ever subscribes (SFU offers, we answer).
// v0.14.0: publish on demand. The SFU binds a track to its media only if RTP starts
// right after the track is registered; an "empty slot" registered at join and filled
// minutes later shows as active but is never pullable ("not_found_track_error").
// So mic/cam/screen each register a fresh track with live media the moment they turn
// on, and are closed when they turn off — exactly the path screen share always used.
// The DO tells us who to pull (`media` message); we never decide that ourselves.
import type { MediaPeer } from '../src/protocol';

interface PulledTrack { mid: string; track: MediaStreamTrack | null; at: number }
interface PulledPeer { rtc: string; tracks: Map<string, PulledTrack> }
type Kind = 'mic' | 'cam' | 'screen';
interface Published { track: MediaStreamTrack | null; tr: RTCRtpTransceiver; mid: string }

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${path} → ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

function newPc(): RTCPeerConnection {
  return new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.cloudflare.com:3478' }], bundlePolicy: 'max-bundle' });
}

export class RtcClient {
  /** publish connection — this is the session id peers use to pull our tracks */
  pc: RTCPeerConnection | null = null;
  sessionId: string | null = null;
  /** subscribe connection */
  private sub: RTCPeerConnection | null = null;
  private subSession: string | null = null;

  private queue: Promise<unknown> = Promise.resolve();
  private published = new Map<Kind, Published>();
  private pulled = new Map<string, PulledPeer>();
  private stopped = false;
  private lastPeers: MediaPeer[] = [];
  private retryTimer: number | null = null;
  private watchdog: number | null = null;

  /** Called with a live track when a remote track arrives, or null when it's gone. */
  onRemoteTrack: (peerId: string, trackName: string, track: MediaStreamTrack | null) => void = () => {};
  /** Called whenever our session id / published track list changes (so the DO can be told). */
  onPublishedChange: (tracks: string[]) => void = () => {};
  onScreenEnded: () => void = () => {};

  constructor(private userId: string) {}

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.queue.then(fn, fn);
    this.queue = next.catch((e) => console.warn('[rtc] op failed', e));
    return next;
  }

  /**
   * v1.21.2: the publish session is NOT made up front any more: the SFU quietly drops a session that hasn't negotiated
   * within a few seconds, so someone who unmuted after sitting in the office a while first waited ~6 s for a 410 and a
   * rebuild. It's made the moment the first track goes out (peers are told the id then, with the track).
   */
  async start(): Promise<void> {
    if (this.lastPeers.length) void this.syncPeers(this.lastPeers);
  }

  /** when the current publish session was made, and whether it has carried a negotiation yet */
  private pubAt = 0;
  private pubNegotiated = false;

  private async ensurePub(): Promise<RTCPeerConnection> {
    const fresh = this.pubNegotiated || Date.now() - this.pubAt < 4000;
    if (this.pc && this.sessionId && fresh && this.pc.connectionState !== 'failed' && this.pc.connectionState !== 'closed') return this.pc;
    try { this.pc?.close(); } catch { /* ignore */ }
    this.published.clear();
    const pc = this.pc = newPc();
    pc.onconnectionstatechange = () => { console.log('[rtc] pub', pc.connectionState); this.watchConn(pc, () => this.recoverPub(pc)); };
    const a = await api<{ sessionId: string }>('POST', '/api/rtc/session');
    this.sessionId = a.sessionId;
    this.pubAt = Date.now(); this.pubNegotiated = false;
    return this.pc;
  }

  /**
   * The SFU drops a session that never negotiates, so the subscribe connection is
   * created only when there is something to pull, and rebuilt if it has gone stale.
   */
  private async ensureSub(): Promise<RTCPeerConnection> {
    if (this.sub && this.subSession && this.sub.connectionState !== 'failed' && this.sub.connectionState !== 'closed') return this.sub;
    this.resetSub();
    const sub = this.sub = newPc();
    sub.onconnectionstatechange = () => { console.log('[rtc] sub', sub.connectionState); this.watchConn(sub, () => this.recoverSub(sub)); };
    const b = await api<{ sessionId: string }>('POST', '/api/rtc/session');
    this.subSession = b.sessionId;
    return this.sub;
  }

  /**
   * v1.21.2: a connection that drops (Wi-Fi blip, network change, laptop sleep) used to stay dead until someone toggled
   * their mic. Now 'failed', or 'disconnected' for 4 s, rebuilds it: the publish side republishes every live track on a
   * new session (peers are told and re-pull), the subscribe side re-pulls everyone.
   */
  private watchConn(pc: RTCPeerConnection, recover: () => void): void {
    if (pc.connectionState === 'failed') { recover(); return; }
    if (pc.connectionState === 'disconnected') {
      window.setTimeout(() => { if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') recover(); }, 4000);
    }
  }

  private recoverPub(pc: RTCPeerConnection): void {
    void this.enqueue(async () => {
      if (this.stopped || pc !== this.pc) return;
      console.warn('[rtc] publish connection lost, rebuilding');
      const desired = new Map<Kind, MediaStreamTrack>();
      for (const k of ['mic', 'cam', 'screen'] as Kind[]) { const t = this.published.get(k)?.track; if (t?.readyState === 'live') desired.set(k, t); }
      if (desired.size) { await this.rebuildPub(desired); return; }
      // (nothing live: just drop it; a new session is made when a track next goes out)
      try { this.pc?.close(); } catch { /* ignore */ }
      this.pc = null; this.sessionId = null; this.published.clear();
      this.onPublishedChange(this.publishedTrackNames());
    });
  }

  private recoverSub(sub: RTCPeerConnection): void {
    void this.enqueue(async () => {
      if (this.stopped || sub !== this.sub) return;
      console.warn('[rtc] subscribe connection lost, pulling everyone again');
      this.resetSub();
    }).then(() => this.syncPeers(this.lastPeers));
  }

  private resetSub(): void {
    for (const [peerId, pp] of this.pulled) for (const name of pp.tracks.keys()) this.onRemoteTrack(peerId, name, null);
    this.pulled.clear();
    try { this.sub?.close(); } catch { /* ignore */ }
    this.sub = null; this.subSession = null;
  }

  /** Re-run the last sync; pulls anything missing or dead. Safe to call often. */
  resync(): Promise<void> {
    return this.syncPeers(this.lastPeers);
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null || this.stopped) return;
    this.retryTimer = window.setTimeout(() => { this.retryTimer = null; void this.syncPeers(this.lastPeers); }, 1500);
  }

  /** Track names peers may pull right now — a slot's media is live. */
  publishedTrackNames(): string[] {
    return (['mic', 'cam', 'screen'] as Kind[])
      .filter((k) => this.published.get(k)?.track?.readyState === 'live')
      .map((k) => `${this.userId}-${k}`);
  }

  /**
   * Enable a track. The FIRST time a kind is used its transceiver is created and the
   * session renegotiated once; after that the slot is reused via replaceTrack — no new
   * m-line, no /tracks/close, no re-offer — so toggling can never 410 or rebuild the
   * session (which would orphan the peers pulling us).
   */
  private publishTrack(kind: Kind, track: MediaStreamTrack): Promise<boolean> {
    return this.enqueue(async () => {
      if (this.stopped) return false;
      const existing = this.published.get(kind);
      if (existing) {
        try {
          await existing.tr.sender.replaceTrack(track);
          existing.track = track;
          this.onPublishedChange(this.publishedTrackNames());
          return true;
        } catch (e) { console.warn(`[rtc] re-enable ${kind} failed`, e); return false; }
      }
      // desired = everything already live + this new track, in a fixed order.
      const desired = new Map<Kind, MediaStreamTrack>();
      for (const k of ['mic', 'cam', 'screen'] as Kind[]) {
        const p = this.published.get(k);
        if (p?.track?.readyState === 'live') desired.set(k, p.track);
      }
      desired.set(kind, track);
      // Try to add just this track to the current session; on any failure rebuild the
      // whole publish session from scratch (an ERROR_CONTENT offer wedges the PC).
      if (await this.tryAddTrack(kind, track)) return true;
      await this.rebuildPub(desired);
      return this.published.get(kind)?.track === track;
    });
  }

  /** Add one track to the existing publish session. Returns false on any error. */
  private async tryAddTrack(kind: Kind, track: MediaStreamTrack): Promise<boolean> {
    let pc: RTCPeerConnection;
    try { pc = await this.ensurePub(); } catch { return false; }
    let tr: RTCRtpTransceiver | null = null;
    try {
      tr = pc.addTransceiver(track, { direction: 'sendonly' });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await api<{ sessionDescription: RTCSessionDescriptionInit; tracks?: { errorCode?: string; errorDescription?: string }[] }>(
        'POST', '/api/rtc/tracks',
        { sessionId: this.sessionId, sessionDescription: { type: 'offer', sdp: pc.localDescription!.sdp }, tracks: [{ location: 'local', mid: tr.mid, trackName: `${this.userId}-${kind}` }] },
      );
      const err = (res.tracks ?? []).find((t) => t.errorCode);
      if (err) throw new Error(`${err.errorCode}: ${err.errorDescription}`);
      await pc.setRemoteDescription(res.sessionDescription);
      this.pubNegotiated = true;
      this.published.set(kind, { track, tr, mid: tr.mid! });
      await this.untilFlowing(pc, [tr.sender]);
      this.onPublishedChange(this.publishedTrackNames());
      return true;
    } catch (e) {
      console.warn(`[rtc] add ${kind} failed, will rebuild`, e);
      try { tr?.stop(); } catch { /* ignore */ }
      return false;
    }
  }

  /**
   * v1.18.5: peers are only told about a newly published track once its media is reaching the SFU (connection up and
   * RTP going out). Told earlier, a peer could pull it before any RTP and get a track that stays silent for good.
   * Gives up waiting after 6 s (the subscriber's watchdog covers the rest).
   */
  private async untilFlowing(pc: RTCPeerConnection, senders: RTCRtpSender[]): Promise<void> {
    const t0 = Date.now();
    while (Date.now() - t0 < 6000 && !this.stopped) {
      if (pc.connectionState === 'connected') {
        let sent = true;
        for (const sd of senders) {
          let b = 0;
          try { (await sd.getStats()).forEach((r: { type: string; bytesSent?: number }) => { if (r.type === 'outbound-rtp') b += r.bytesSent ?? 0; }); } catch { /* ignore */ }
          if (!b) sent = false;
        }
        if (sent) return;
      }
      await new Promise((r) => setTimeout(r, 150));
    }
  }

  /** Rebuild the publish session and (re)publish all desired live tracks in one offer. */
  private async rebuildPub(desired: Map<Kind, MediaStreamTrack>): Promise<boolean> {
    try { this.pc?.close(); } catch { /* ignore */ }
    this.pc = null; this.sessionId = null; this.published.clear();
    if (this.stopped) return false;
    let pc: RTCPeerConnection;
    try { pc = await this.ensurePub(); } catch (e) { console.warn('[rtc] rebuild session failed', e); return false; }
    const order: Kind[] = (['mic', 'cam', 'screen'] as Kind[]).filter((k) => desired.has(k));
    const added: { kind: Kind; tr: RTCRtpTransceiver; track: MediaStreamTrack }[] = [];
    for (const k of order) { const t = desired.get(k)!; added.push({ kind: k, tr: pc.addTransceiver(t, { direction: 'sendonly' }), track: t }); }
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await api<{ sessionDescription: RTCSessionDescriptionInit; tracks?: { errorCode?: string; errorDescription?: string }[] }>(
        'POST', '/api/rtc/tracks',
        { sessionId: this.sessionId, sessionDescription: { type: 'offer', sdp: pc.localDescription!.sdp }, tracks: added.map((a) => ({ location: 'local', mid: a.tr.mid, trackName: `${this.userId}-${a.kind}` })) },
      );
      const err = (res.tracks ?? []).find((t) => t.errorCode);
      if (err) throw new Error(`${err.errorCode}: ${err.errorDescription}`);
      await pc.setRemoteDescription(res.sessionDescription);
      this.pubNegotiated = true;
      for (const a of added) this.published.set(a.kind, { track: a.track, tr: a.tr, mid: a.tr.mid! });
      await this.untilFlowing(pc, added.map((a) => a.tr.sender));
      this.onPublishedChange(this.publishedTrackNames());
      return true;
    } catch (e) {
      console.warn('[rtc] rebuild publish failed', e);
      return false;
    }
  }

  /**
   * Disable a track: stop the hardware (camera light off) and pause the sender, but
   * KEEP the transceiver/slot so re-enabling is a plain replaceTrack. The track name
   * drops out of publishedTrackNames while paused so peers stop pulling silence.
   */
  private unpublishTrack(kind: Kind): Promise<void> {
    return this.enqueue(async () => {
      const p = this.published.get(kind);
      if (!p?.track) return;
      p.track.stop();
      p.track = null;
      try { await p.tr.sender.replaceTrack(null); } catch { /* ignore */ }
      this.onPublishedChange(this.publishedTrackNames());
    });
  }

  /** Turn the mic on/off. Off stops the device. Resolves to the resulting state. */
  async setMic(on: boolean): Promise<boolean> {
    if (!on) { await this.unpublishTrack('mic'); return false; }
    if (this.micLive()) return true;
    let track: MediaStreamTrack;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      track = s.getAudioTracks()[0];
    } catch (e) { console.warn('[rtc] mic failed', e); return false; }
    track.onended = () => { void this.unpublishTrack('mic'); };
    const ok = await this.publishTrack('mic', track);
    if (!ok) track.stop();
    return ok;
  }

  /** Turn the camera on/off. Off stops the device (camera light goes out). */
  async setCam(on: boolean): Promise<boolean> {
    if (!on) { await this.unpublishTrack('cam'); return false; }
    if (this.camLive()) return true;
    let track: MediaStreamTrack;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240, frameRate: 15, facingMode: 'user' } });
      track = s.getVideoTracks()[0];
    } catch (e) { console.warn('[rtc] cam failed', e); return false; }
    track.onended = () => { void this.unpublishTrack('cam'); };
    const ok = await this.publishTrack('cam', track);
    if (!ok) track.stop();
    return ok;
  }

  micLive(): boolean { return this.published.get('mic')?.track?.readyState === 'live'; }
  camLive(): boolean { return this.published.get('cam')?.track?.readyState === 'live'; }
  localCamTrack(): MediaStreamTrack | undefined { return this.published.get('cam')?.track ?? undefined; }
  localScreenTrack(): MediaStreamTrack | undefined { const t = this.published.get('screen')?.track; return t && t.readyState === 'live' ? t : undefined; }

  async startScreen(): Promise<boolean> {
    if (this.published.get('screen')?.track?.readyState === 'live') return true;
    let track: MediaStreamTrack;
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false });
      track = stream.getVideoTracks()[0];
    } catch { return false; }
    track.onended = () => { void this.unpublishTrack('screen'); this.onScreenEnded(); };
    const ok = await this.publishTrack('screen', track);
    if (!ok) track.stop();
    return ok;
  }

  stopScreen(): Promise<void> {
    return this.unpublishTrack('screen');
  }

  /** Reconcile pulled tracks (on the subscribe connection) with the DO's desired peer list. */
  syncPeers(peers: MediaPeer[]): Promise<void> {
    this.lastPeers = peers;
    return this.enqueue(async () => {
      if (this.stopped) return;
      const want = new Map(peers.map((p) => [p.id, p]));
      if (!this.sub && !peers.some((p) => p.tracks.length)) return; // nothing to pull yet, don't open a session
      let pc: RTCPeerConnection;
      try { pc = await this.ensureSub(); } catch (e) { console.warn('[rtc] sub session failed', e); this.scheduleRetry(); return; }

      const closeMids: string[] = [];
      for (const [peerId, pp] of this.pulled) {
        const w = want.get(peerId);
        for (const [name, pt] of pp.tracks) {
          const dead = pt.track?.readyState === 'ended';
          if (!w || w.rtc !== pp.rtc || !w.tracks.includes(name) || dead) {
            closeMids.push(pt.mid);
            pp.tracks.delete(name);
            this.onRemoteTrack(peerId, name, null);
          }
        }
        if (pp.tracks.size === 0) this.pulled.delete(peerId);
      }
      if (closeMids.length) {
        try {
          await api('PUT', '/api/rtc/close', { sessionId: this.subSession, tracks: closeMids.map((mid) => ({ mid })), force: true });
        } catch (e) { console.warn('[rtc] close failed', e); }
      }

      const remote: { location: 'remote'; sessionId: string; trackName: string }[] = [];
      for (const p of peers) {
        const have = this.pulled.get(p.id);
        for (const name of p.tracks) {
          if (have && have.rtc === p.rtc && have.tracks.has(name)) continue;
          remote.push({ location: 'remote', sessionId: p.rtc, trackName: name });
        }
      }
      if (!remote.length) return;

      let res: {
        requiresImmediateRenegotiation?: boolean;
        sessionDescription?: RTCSessionDescriptionInit;
        tracks?: { mid?: string; sessionId: string; trackName: string; errorCode?: string; errorDescription?: string }[];
      };
      try {
        res = await api('POST', '/api/rtc/tracks', { sessionId: this.subSession, tracks: remote });
        if (res.requiresImmediateRenegotiation && res.sessionDescription) {
          await pc.setRemoteDescription(res.sessionDescription);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await api('PUT', '/api/rtc/renegotiate', { sessionId: this.subSession, sessionDescription: { type: 'answer', sdp: pc.localDescription!.sdp } });
        }
      } catch (e) {
        console.warn('[rtc] pull failed, will retry', e);
        if (String(e).includes('410') || String(e).includes('session_error')) this.resetSub();
        this.scheduleRetry();
        return;
      }

      let anyError = false;
      for (const t of res.tracks ?? []) {
        if (!t.mid) { console.warn('[rtc] pull error', t.errorCode, t.errorDescription); anyError = true; continue; }
        const peer = peers.find((p) => p.rtc === t.sessionId);
        if (!peer) continue;
        const tr = pc.getTransceivers().find((x) => x.mid === t.mid);
        const track = tr?.receiver.track ?? null;
        let pp = this.pulled.get(peer.id);
        if (!pp) { pp = { rtc: peer.rtc, tracks: new Map() }; this.pulled.set(peer.id, pp); }
        pp.tracks.set(t.trackName, { mid: t.mid, track, at: Date.now() });
        if (track) this.onRemoteTrack(peer.id, t.trackName, track);
      }
      if (anyError) this.scheduleRetry();
      this.startWatchdog();
    });
  }

  /**
   * v1.18.5: a pull made just as the other side turns their mic on can be answered with a track that never carries
   * media (the SFU binds it before RTP flows), and nothing would ever re-pull it: two otters side by side, one silent
   * to the other for good. Every 1.5 s, a pulled track that has received no bytes 3 s after it was pulled is closed and
   * pulled again.
   */
  private startWatchdog(): void {
    if (this.watchdog !== null || this.stopped) return;
    this.watchdog = window.setInterval(() => { void this.checkSilent(); }, 1500);
  }

  private async checkSilent(): Promise<void> {
    if (!this.sub || this.stopped) return;
    const bytes = new Map<string, number>();
    try {
      const stats = await this.sub.getStats();
      stats.forEach((r: { type: string; mid?: string; bytesReceived?: number }) => { if (r.type === 'inbound-rtp' && r.mid) bytes.set(r.mid, r.bytesReceived ?? 0); });
    } catch { return; }
    let stale = false;
    for (const pp of this.pulled.values()) {
      for (const pt of pp.tracks.values()) {
        if (Date.now() - pt.at > 3000 && !bytes.get(pt.mid) && pt.track?.readyState !== 'ended') { pt.track?.stop(); stale = true; }
      }
    }
    if (stale) { console.warn('[rtc] a pulled track carried no media, pulling it again'); void this.syncPeers(this.lastPeers); }
  }

  stop(): void {
    this.stopped = true;
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.watchdog !== null) { clearInterval(this.watchdog); this.watchdog = null; }
    for (const p of this.published.values()) p.track?.stop();
    try { this.pc?.close(); } catch { /* ignore */ }
    try { this.sub?.close(); } catch { /* ignore */ }
    this.pc = null; this.sub = null;
  }
}
